import { ApiError } from "@/pages/auth/api";
import { API_BASE_URL } from "@/shared/config/apiBaseUrl";

export type DevlogPost = {
  post_id: string;
  title: string;
  body: string;
  author_username: string;
  created_at: string;
  updated_at: string;
};

export type DevlogPostPage = {
  items: DevlogPost[];
  next_cursor?: string | null;
};

export type DevlogPostPayload = {
  title: string;
  body: string;
};

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let detail = `request failed with status ${response.status}`;
    try {
      const payload = (await response.json()) as { detail?: string };
      if (payload.detail) {
        detail = payload.detail;
      }
    } catch {
      // Keep fallback message when response body is not JSON.
    }
    throw new ApiError(detail, response.status);
  }

  return (await response.json()) as T;
}

export async function listDevlogPosts(before?: string | null): Promise<DevlogPostPage> {
  const params = new URLSearchParams({ limit: "20" });
  if (before) {
    params.set("before", before);
  }
  const response = await fetch(`${API_BASE_URL}/api/devlog/posts?${params.toString()}`);
  return parseJson<DevlogPostPage>(response);
}

export async function createDevlogPost(
  sessionToken: string,
  payload: DevlogPostPayload,
): Promise<DevlogPost> {
  const response = await fetch(`${API_BASE_URL}/api/devlog/posts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify(payload),
  });
  return parseJson<DevlogPost>(response);
}

export async function updateDevlogPost(
  sessionToken: string,
  postId: string,
  payload: DevlogPostPayload,
): Promise<DevlogPost> {
  const response = await fetch(`${API_BASE_URL}/api/devlog/posts/${encodeURIComponent(postId)}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify(payload),
  });
  return parseJson<DevlogPost>(response);
}

export async function deleteDevlogPost(sessionToken: string, postId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/devlog/posts/${encodeURIComponent(postId)}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
  });

  if (!response.ok) {
    let detail = `request failed with status ${response.status}`;
    try {
      const payload = (await response.json()) as { detail?: string };
      if (payload.detail) {
        detail = payload.detail;
      }
    } catch {
      // Keep fallback message when response body is not JSON.
    }
    throw new ApiError(detail, response.status);
  }
}

